import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Checkbox } from "@/components/Checkbox/Checkbox";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { classNames } from "@/utils/classNames";
import {
	useGetScoreCompanyCategoriesQuery,
	useSetScoreCompanyCategoriesMutation,
} from "@/pages/Private/redux/list/list.api";
import { useGetScoreQuery } from "@/pages/Private/redux/score/score.api";
import { useAppSelector } from "@/redux/hooks";
import { listSelector } from "@/pages/Private/redux/list/list.slice";

interface CompanySegmentsSectionProps {
	listId: number;
}

export const CompanySegmentsSection: FunctionComponent<CompanySegmentsSectionProps> = ({
	listId,
}) => {
	const { t } = useTranslation();
	const [expanded, setExpanded] = useState<boolean>(true);

	const { data: score, isLoading: isLoadingScore } = useGetScoreQuery();
	const { isLoading: isLoadingSelected } = useGetScoreCompanyCategoriesQuery(listId);
	const [setScoreCompanyCategories] = useSetScoreCompanyCategoriesMutation();

	const { scoreCompanyCategories } = useAppSelector(listSelector);

	const allCategories = score?.prospectCompanyCategories ?? [];
	const selectedIds = scoreCompanyCategories.map((c) => c.id);

	const allSelected = allCategories.length > 0 && selectedIds.length === allCategories.length;

	const handleToggleAll = () => {
		if (allCategories.length === 0) {
			return;
		}

		const newIds = allSelected ? [] : allCategories.map((c) => c.id!);

		setScoreCompanyCategories({ id: listId, scoreCompanyCategoryIds: newIds });
	};

	const handleToggle = (id: number) => {
		const newIds = selectedIds.includes(id)
			? selectedIds.filter((sid) => sid !== id)
			: [...selectedIds, id];

		setScoreCompanyCategories({ id: listId, scoreCompanyCategoryIds: newIds });
	};

	if (isLoadingScore || isLoadingSelected) {
		return null;
	}

	if (allCategories.length === 0) {
		return null;
	}

	return (
		<div
			className={classNames(
				"border p-6 mb-8 w-full items-start rounded-xl border-1 border-border-light",
				"flex flex-col text-left gap-6",
				expanded ? "bg-background-light" : "bg-white"
			)}
		>
			<button
				className={classNames(
					"w-full items-center group cursor-pointer flex flex-row text-left gap-6"
				)}
				onClick={() => setExpanded(!expanded)}
			>
				<div className="grow">
					<div className="text-smd text-text font-bold">{t("list.companySegments.title")}</div>
					<div className="text-ssm text-text-meta">{t("list.companySegments.subtitle")}</div>
				</div>
				<div
					className={classNames(
						"flex min-w-[36px] min-h-[36px] !w-[36px] !h-[36px] group-hover:bg-blue-action rounded-xl items-center justify-center border",
						expanded ? "border-border-light bg-white" : "border-transparent bg-background-light"
					)}
				>
					<Icon
						className="text-text-meta group-hover:text-white w-[12px] h-auto"
						icon={expanded ? faChevronUp : faChevronDown}
					/>
				</div>
			</button>

			{expanded && (
				<div className="w-full">
					<div className="flex items-center py-2 px-3 bg-brand-white border border-border-light rounded-t-md">
						<Checkbox isChecked={!!allSelected} onChange={handleToggleAll}>
							<span className="text-xs font-semibold text-text-meta">
								{t("list.companySegments.selectAll")}
							</span>
						</Checkbox>
					</div>

					<div className="bg-white rounded-b-md border border-t-0 border-border-light">
						{allCategories.map((category) => (
							<div
								key={category.id}
								className="flex items-center py-3 px-3 border-b border-border-light last:border-b-0 hover:bg-background-light transition-colors"
							>
								<Checkbox
									isChecked={selectedIds.includes(category.id!)}
									onChange={() => handleToggle(category.id!)}
								>
									<Tooltip content={category.description} direction="bottom">
										<span className="text-sm font-medium text-text cursor-default">
											{category.category}
										</span>
									</Tooltip>
								</Checkbox>
							</div>
						))}
					</div>
				</div>
			)}
		</div>
	);
};
